import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {message} from 'antd';
import {recycleService} from '@/services/recycleService';
import type {
  CreateRecycleValuationConfigurationCommand,
  RecycleCategory,
  RecycleValuationConfiguration,
  RecycleValuationQuery,
  UpdateRecycleValuationConfigurationCommand
} from '@/types/recycle';

// 异步操作
export const fetchCategoryValuations = createAsyncThunk(
  'recycleValuation/fetchCategoryValuations',
  async () => {
    const response = await recycleService.getCategoryValuations();
    return response;
  }
);

export const updateCategoryValuation = createAsyncThunk(
  'recycleValuation/updateCategoryValuation',
  async (data: any) => {
    const response = await recycleService.updateCategoryValuation(data);
    return response;
  }
);

export const fetchValuationConfigurations = createAsyncThunk(
  'recycleValuation/fetchValuationConfigurations',
  async (query: RecycleValuationQuery) => {
    const response = await recycleService.getValuationConfigurations(query);
    return response;
  }
);

export const createValuationConfiguration = createAsyncThunk(
  'recycleValuation/createValuationConfiguration',
  async (data: CreateRecycleValuationConfigurationCommand) => {
    const response = await recycleService.createValuationConfiguration(data);
    return response;
  }
);

export const updateValuationConfiguration = createAsyncThunk(
  'recycleValuation/updateValuationConfiguration',
  async (data: UpdateRecycleValuationConfigurationCommand) => {
    const response = await recycleService.updateValuationConfiguration(data);
    return response;
  }
);

export const deleteValuationConfiguration = createAsyncThunk(
  'recycleValuation/deleteValuationConfiguration',
  async (id: number) => {
    await recycleService.deleteValuationConfiguration(id);
    return id;
  }
);

export const fetchValuationConfigurationById = createAsyncThunk(
  'recycleValuation/fetchValuationConfigurationById',
  async (id: number) => {
    const response = await recycleService.getValuationConfigurationById(id);
    return response;
  }
);

export const generateValuationPrice = createAsyncThunk(
  'recycleValuation/generateValuationPrice',
  async (data: { categoryId: number; itemValues: Record<string, string> }) => {
    const response = await recycleService.generateValuationPrice(data);
    return response;
  }
);

// 状态类型
interface RecycleValuationState {
  categoryValuations: RecycleCategory[];
  configurations: RecycleValuationConfiguration[];
  currentConfiguration: RecycleValuationConfiguration | null;
  generatedPrice: number | null;
  total: number;
  loading: boolean;
  error: string | null;
}

// 初始状态
const initialState: RecycleValuationState = {
  categoryValuations: [],
  configurations: [],
  currentConfiguration: null,
  generatedPrice: null,
  total: 0,
  loading: false,
  error: null,
};

// Slice
const recycleValuationSlice = createSlice({
  name: 'recycleValuation',
  initialState,
  reducers: {
    clearCurrentConfiguration: (state) => {
      state.currentConfiguration = null;
    },
    clearGeneratedPrice: (state) => {
      state.generatedPrice = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchCategoryValuations
    builder
      .addCase(fetchCategoryValuations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategoryValuations.fulfilled, (state, action) => {
        state.loading = false;
        state.categoryValuations = action.payload;
      })
      .addCase(fetchCategoryValuations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取品类估价配置失败';
        message.error(state.error);
      });

    // updateCategoryValuation
    builder
      .addCase(updateCategoryValuation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategoryValuation.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.categoryValuations.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.categoryValuations[index] = action.payload;
        }
        message.success('更新品类估价配置成功');
      })
      .addCase(updateCategoryValuation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '更新品类估价配置失败';
        message.error(state.error);
      });

    // fetchValuationConfigurations
    builder
      .addCase(fetchValuationConfigurations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchValuationConfigurations.fulfilled, (state, action) => {
        state.loading = false;
        state.configurations = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(fetchValuationConfigurations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取估价配置列表失败';
        message.error(state.error);
      });

    // fetchValuationConfigurationById
    builder
      .addCase(fetchValuationConfigurationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchValuationConfigurationById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentConfiguration = action.payload;
      })
      .addCase(fetchValuationConfigurationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取估价配置详情失败';
        message.error(state.error);
      });

    // createValuationConfiguration
    builder
      .addCase(createValuationConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createValuationConfiguration.fulfilled, (state, action) => {
        state.loading = false;
        state.configurations.unshift(action.payload);
        state.total += 1;
        message.success('创建估价配置成功');
      })
      .addCase(createValuationConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '创建估价配置失败';
        message.error(state.error);
      });

    // updateValuationConfiguration
    builder
      .addCase(updateValuationConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateValuationConfiguration.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.configurations.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.configurations[index] = action.payload;
        }
        if (state.currentConfiguration?.id === action.payload.id) {
          state.currentConfiguration = action.payload;
        }
        message.success('更新估价配置成功');
      })
      .addCase(updateValuationConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '更新估价配置失败';
        message.error(state.error);
      });

    // deleteValuationConfiguration
    builder
      .addCase(deleteValuationConfiguration.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteValuationConfiguration.fulfilled, (state, action) => {
        state.loading = false;
        state.configurations = state.configurations.filter(item => item.id !== action.payload);
        state.total -= 1;
        message.success('删除估价配置成功');
      })
      .addCase(deleteValuationConfiguration.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '删除估价配置失败';
        message.error(state.error);
      });

    // generateValuationPrice
    builder
      .addCase(generateValuationPrice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateValuationPrice.fulfilled, (state, action) => {
        state.loading = false;
        state.generatedPrice = action.payload;
        message.success('生成估价价格成功');
      })
      .addCase(generateValuationPrice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '生成估价价格失败';
        message.error(state.error);
      });
  },
});

export const { clearCurrentConfiguration, clearGeneratedPrice, clearError } = recycleValuationSlice.actions;
export default recycleValuationSlice.reducer;
